import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoLight from '../assets/logo-mark.png';
import LogoDark from '../assets/logo-mark-dark.png';

import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Snackbar,
  InputAdornment,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  BusinessOutlined,
  AdminPanelSettingsOutlined,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { USER_ROLES, APP_CONFIG } from '../utils/constants';

const Login = ({ user }) => {
  const isAdmin = user === 'ADMIN';

  const navigate = useNavigate();
  const { login, businessLogin, authError, clearAuthError } = useAuth();
  const { mode } = useThemeMode();

  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (authError) {
      setLocalError(authError);
      setShowSnackbar(true);
    }
  }, [authError]);

  // Reset form + errors when route (user prop) changes
  useEffect(() => {
    setLocalError('');
    clearAuthError();
    setFormData({ email: '', password: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdminLogin = async (email, password) => {
    const result = await login(email, password);
    if (result.success) {
      navigate(result.user.role === USER_ROLES.ADMIN ? '/admin/dashboard' : '/business/dashboard');
    } else {
      setLocalError(result.error);
    }
  };

  const handleBusinessLogin = async (email, password) => {
    const result = await businessLogin(email, password);
    if (!result.success) {
      setLocalError(result.error);
      return;
    }
    if (result.requiresPasswordChange) {
      if (!result.token || result.token === 'undefined') {
        setLocalError('No temporary token received for password change');
        return;
      }
      navigate('/change-password', {
        state: {
          isFirstLogin: true,
          businessName: result.business?.businessName,
          tempToken: result.token,
        },
      });
      return;
    }
    navigate('/business/dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    clearAuthError();
    setLoading(true);
    try {
      if (isAdmin) {
        await handleAdminLogin(formData.email, formData.password);
      } else {
        await handleBusinessLogin(formData.email, formData.password);
      }
    } catch (err) {
      console.error('Unexpected login error:', err);
      setLocalError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
    clearAuthError();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        py: { xs: 3, sm: 4 },
        px: 2,
      }}
    >
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {authError}
        </Alert>
      </Snackbar>

      <Container maxWidth="xs" disableGutters>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Logo / Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Box
                component="img"
                src={mode === 'dark' ? LogoDark : LogoLight}
                alt="OK200 Logo"
                sx={{
                  height: 56,
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <Typography variant="h5" component="h1" fontWeight={700} sx={{ mb: 1 }}>
              {APP_CONFIG.APP_NAME} Portal
            </Typography>

            {/* Role badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: 99,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              {isAdmin
                ? <AdminPanelSettingsOutlined sx={{ fontSize: 16 }} />
                : <BusinessOutlined sx={{ fontSize: 16 }} />}
              <Typography variant="caption" fontWeight={600}>
                {isAdmin ? 'Admin Login' : 'Business Login'}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Sign in to your account
            </Typography>
          </Box>

          {localError && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>
              {localError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              autoComplete="email"
              helperText={!isAdmin ? 'Enter your registered business email' : undefined}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined color="action" fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
              size="large"
              sx={{ mt: 3, py: 1.3, borderRadius: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                `Sign In as ${isAdmin ? 'Admin' : 'Business User'}`
              )}
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              display="block"
              sx={{ mt: 3 }}
            >
              © {new Date().getFullYear()} {APP_CONFIG.APP_NAME} Portal. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
